import React, {useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {FaIcon} from '../components'

// React twin of _gg_input.html.haml + initInputs(). Controlled input with
// optional client-side validation: `validate` receives the trimmed value and
// returns {state: 'error'|'success', message} (or null). It runs on blur,
// then re-runs debounced while typing once a state is showing.
export const GGInput = ({
  name,
  value,
  onChange,
  label,
  placeholder,
  hint,
  state,
  message,
  disabled = false,
  clearable = true,
  icon,
  type = 'text',
  validate,
  validateDebounce = 400,
  className,
}) => {
  const [ internalState, setInternalState ] = useState(null)
  const timer = useRef(null)
  const validatedRef = useRef(false)

  const effectiveState = internalState?.state ?? state
  const effectiveMessage = internalState ? internalState.message : (message || hint)
  const classes = [
    'gg-input',
    effectiveState && `gg-input--${effectiveState}`,
    disabled && 'gg-input--disabled',
    className,
  ].filter(Boolean).join(' ')

  const runValidation = (val) => {
    if (!validate) { return }
    validatedRef.current = true
    setInternalState(val.trim().length ? validate(val.trim()) : null)
  }

  const handleChange = (e) => {
    onChange(e.target.value)
    if (validate && validatedRef.current) {
      clearTimeout(timer.current)
      timer.current = setTimeout(() => runValidation(e.target.value), validateDebounce)
    }
  }

  const handleClear = () => {
    clearTimeout(timer.current)
    validatedRef.current = false
    setInternalState(null)
    onChange('')
  }

  return (
    <div className={classes}>
      {label && <label className="gg-input__label" htmlFor={name}>{label}</label>}

      <div className="gg-input__field-wrap">
        {icon && (
          <span className="gg-input__icon gg-input__icon--lead">
            <FaIcon icon={icon} style={{width: '14px', height: '14px'}}/>
          </span>
        )}
        <input
          className="gg-input__field"
          id={name}
          name={name}
          type={type}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onChange={handleChange}
          onBlur={(e) => { clearTimeout(timer.current); runValidation(e.target.value) }}
        />
        {clearable && !disabled && value?.length > 0 && (
          <span className="gg-input__icon gg-input__icon--clear" onClick={handleClear}>
            <FaIcon icon="xmark-o" usePro/>
          </span>
        )}
        {effectiveState === 'success' && (
          <span className="gg-input__icon gg-input__icon--success">
            <FaIcon icon="check-o" usePro/>
          </span>
        )}
      </div>

      {effectiveMessage && <span className="gg-input__message">{effectiveMessage}</span>}
    </div>
  )
}

GGInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  hint: PropTypes.string,
  state: PropTypes.oneOf([ 'error', 'success' ]),
  message: PropTypes.string,
  disabled: PropTypes.bool,
  clearable: PropTypes.bool,
  icon: PropTypes.string,
  type: PropTypes.string,
  validate: PropTypes.func,
  validateDebounce: PropTypes.number,
  className: PropTypes.string,
}
